// externals
import type { FC } from 'react';
import { useCallback } from 'react';

// libraries
import { PriceInput } from '@makemydeal/dr-shared-components/price-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// interfaces/types
import type { ColumnMapping, InputChangeInterstateEventHandler } from '../manualAccessoriesTypes';
import type { Accessory } from '@makemydeal/dr-dash-types';

// consts/enums
import * as deskingConstants from '../../../constants';

export type ManualAccessoryResidualFieldStateProps = {
    disabled: boolean;
    index: number;
    residual: string;
    residualColumnMapping: ColumnMapping<number>;
    accessories?: Accessory[];
    hasError?: boolean;
    errorMessage?: string;
};

export type ManualAccessoryResidualFieldDispatchProps = {
    handleInputChange: InputChangeInterstateEventHandler;
    handleOnBlur: (fieldName: string) => void;
    handleKeyPress: (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>,
        fieldName: string
    ) => void;
};

export type ManualAccessoryResidualFieldProps = ManualAccessoryResidualFieldDispatchProps & ManualAccessoryResidualFieldStateProps;

export const ManualAccessoryResidualField: FC<ManualAccessoryResidualFieldProps> = ({
    disabled,
    index,
    residual,
    residualColumnMapping: { fieldName },
    handleInputChange,
    handleOnBlur,
    handleKeyPress,
    hasError,
    errorMessage
}) => {
    const handleResidualChange = useCallback(
        (e: any) => {
            handleInputChange(e, fieldName);
        },
        [fieldName]
    );

    const handleResidualBlur = useCallback(() => {
        handleOnBlur(fieldName);
    }, [fieldName, handleOnBlur]);

    return (
        <PriceInput
            sx={{ minWidth: '108px' }}
            size="sm"
            allowDecimal={true}
            displayLabel={true}
            data-testid={`${fieldName}-${index}`}
            label={deskingConstants.RESIDUAL}
            className="base-accessories-label"
            name={`${fieldName}-${index}`}
            onChange={handleResidualChange}
            onBlur={handleResidualBlur}
            onKeyDown={(e) => {
                handleKeyPress(e, fieldName);
            }}
            value={residual}
            disabled={disabled}
            hasError={hasError}
            errorMessage={errorMessage}
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
        />
    );
};
